import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {DropdownItemIconEmoji} from '../../../../components/common/dropdowns/dropdownItemIconEmoji';
import {Emoji} from '../../../../components/common/emoji/emoji';
import {
  getPreviewerEmojiDefinition,
  getPreviewerLinkTypeLabel,
} from '../../../../models/api/previewerHelpers';
import {PreviewerLinkTypesEnum} from '../../../../models/api/previewerModel';
import {PreviewerEditorStateActions} from '../../../editors/previewerEditor/usePreviewerEditorState';

/*
 * Props.
 */

interface PreviewerLinkTypeInputProps {
  linkType: PreviewerLinkTypesEnum;
  onUpdate: PreviewerEditorStateActions['onUpdateLinkType'];
  isReadOnly: boolean;
}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0px;
`;

const StyledValueWrapperDiv = styled.div`
  display: flex;
  align-items: center;

  > img {
    margin-right: 8px;
  }
`;

/*
 * Component.
 */

export const PreviewerLinkTypeInput: FC<PreviewerLinkTypeInputProps> = (props) => {
  const {linkType, onUpdate, isReadOnly} = props;

  return (
    <StyledDropdownBoxCoordinator
      value={renderValue(linkType)}
      renderDropdown={(popoverProps) => renderDropdown(popoverProps, linkType, onUpdate)}
      isDisabled={isReadOnly}
    />
  );
};

/*
 * Helpers.
 */

function renderValue(linkType: PreviewerLinkTypesEnum) {
  const emoji = getPreviewerEmojiDefinition(linkType);

  return (
    <StyledValueWrapperDiv>
      {emoji && <Emoji size={20} {...emoji} />}
      {getPreviewerLinkTypeLabel(linkType)}
    </StyledValueWrapperDiv>
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  selectedLinkType: PreviewerLinkTypesEnum,
  onSelect: HandlerOf<PreviewerLinkTypesEnum>,
) {
  return (
    <SimpleDropdown {...popoverProps}>
      {collectEnumValues(PreviewerLinkTypesEnum).map((linkType) =>
        renderDropdownItem(linkType, linkType === selectedLinkType, onSelect),
      )}
    </SimpleDropdown>
  );
}

function renderDropdownItem(
  linkType: PreviewerLinkTypesEnum,
  isChecked: boolean,
  onSelect: HandlerOf<PreviewerLinkTypesEnum>,
) {
  const emoji = getPreviewerEmojiDefinition(linkType);

  return (
    <DropdownItem
      key={linkType}
      isChecked={isChecked}
      onSelect={() => onSelect(linkType)}
      shouldReserveRightIcon
    >
      {emoji && <DropdownItemIconEmoji {...emoji} />}
      <DropdownItemContent isChecked={isChecked}>{getPreviewerLinkTypeLabel(linkType)}</DropdownItemContent>
    </DropdownItem>
  );
}
